import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { MODULES } from '@/utils/permissions';

// ─────────────────────────────────────────────────────────────────────────────
// Party balances card — money to receive (green ↓) vs money to pay (red ↑).
// Clicking a party opens their full transaction ledger.
// ─────────────────────────────────────────────────────────────────────────────

interface PartyBalance {
  id: string;
  name: string;
  type: string;
  balance: number;
  direction: 'RECEIVE' | 'PAY';
}

interface PartyBalancesData {
  receivablesTotal: number;
  payablesTotal: number;
  parties: PartyBalance[];
}

const fetchPartyBalances = async (): Promise<PartyBalancesData> => {
  const res = await axios.get(
    `${import.meta.env.VITE_API_URL}/api/v1/reports/party-balances`,
    {
      params: { limit: 6 },
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    },
  );
  return (
    res.data?.data ?? { receivablesTotal: 0, payablesTotal: 0, parties: [] }
  );
};

function formatAmount(val: number): string {
  const abs = Math.abs(val);
  if (abs >= 100_000) return `₹${(abs / 100_000).toFixed(1)}L`;
  if (abs >= 1_000) return `₹${(abs / 1_000).toFixed(1)}K`;
  return `₹${abs.toFixed(0)}`;
}

const TYPE_BADGE: Record<string, string> = {
  CUSTOMER: 'bg-indigo-50 text-indigo-600',
  SUPPLIER: 'bg-amber-50 text-amber-700',
  BOTH: 'bg-slate-100 text-slate-600',
};

export default function PartyBalancesCard() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canViewLedger = hasPermission(MODULES.REPORT_PARTY_LEDGER, 'view');

  const { data, isLoading } = useQuery({
    queryKey: ['party-balances'],
    queryFn: fetchPartyBalances,
    staleTime: 60_000,
  });

  const parties = data?.parties ?? [];

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
      {/* ── Header ── */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0]">
        <div>
          <h3 className="text-sm font-semibold text-[#1e293b]">
            Party Balances
          </h3>
          <p className="text-xs text-[#64748b] mt-0.5">
            Who owes you · who you owe
          </p>
        </div>
        <button
          onClick={() => navigate('/masters/parties')}
          className="text-xs font-medium text-[#4f46e5] hover:text-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          View all
        </button>
      </div>

      {/* ── Totals ── */}
      <div className="grid grid-cols-2 gap-3 px-5 py-3 border-b border-[#f1f5f9]">
        <div className="flex items-center gap-2.5 rounded-lg bg-green-50 px-3 py-2">
          <i className="ri-arrow-down-circle-line text-lg text-green-600" />
          <div>
            <p className="text-[11px] font-medium text-green-700 uppercase tracking-wide">
              To Receive
            </p>
            <p className="text-sm font-bold text-green-700">
              {isLoading ? '…' : formatAmount(data?.receivablesTotal ?? 0)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 rounded-lg bg-red-50 px-3 py-2">
          <i className="ri-arrow-up-circle-line text-lg text-red-500" />
          <div>
            <p className="text-[11px] font-medium text-red-600 uppercase tracking-wide">
              To Pay
            </p>
            <p className="text-sm font-bold text-red-600">
              {isLoading ? '…' : formatAmount(data?.payablesTotal ?? 0)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Skeleton ── */}
      {isLoading && (
        <div className="divide-y divide-[#f1f5f9]">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-5 py-3">
              <div className="w-8 h-8 bg-slate-100 rounded-full animate-pulse" />
              <div className="flex-1 h-3 bg-slate-100 rounded animate-pulse" />
              <div className="w-16 h-3 bg-slate-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      )}

      {/* ── Empty state ── */}
      {!isLoading && parties.length === 0 && (
        <div className="flex flex-col items-center justify-center py-10 text-[#94a3b8]">
          <i className="ri-user-received-2-line text-4xl mb-2" />
          <p className="text-sm font-medium">No outstanding balances</p>
        </div>
      )}

      {/* ── Party rows ── */}
      {!isLoading && parties.length > 0 && (
        <div className="divide-y divide-[#f1f5f9]">
          {parties.map((p) => {
            const receivable = p.direction === 'RECEIVE';
            const badge =
              TYPE_BADGE[(p.type || '').toUpperCase()] ??
              'bg-slate-100 text-slate-600';
            return (
              <div
                key={p.id}
                onClick={() =>
                  canViewLedger &&
                  navigate(`/reports/party-ledger?partyId=${p.id}`)
                }
                className={`flex items-center gap-3 px-5 py-2.5 transition-colors ${
                  canViewLedger ? 'cursor-pointer hover:bg-[#f8fafc]' : ''
                }`}
                title={canViewLedger ? 'View transaction ledger' : undefined}
              >
                <div className="w-8 h-8 rounded-full bg-[#eef2ff] flex items-center justify-center text-sm font-semibold text-[#4f46e5] shrink-0">
                  {(p.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1e293b] truncate">
                    {p.name}
                  </p>
                  <span
                    className={`inline-block text-[10px] font-medium px-1.5 py-px rounded ${badge}`}
                  >
                    {p.type}
                  </span>
                </div>
                <div className="text-right shrink-0">
                  <p
                    className={`text-sm font-bold ${receivable ? 'text-green-600' : 'text-red-500'}`}
                  >
                    <i
                      className={`${receivable ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} text-xs`}
                    />{' '}
                    {formatAmount(p.balance)}
                  </p>
                  <p className="text-[10px] text-[#94a3b8]">
                    {receivable ? 'To receive' : 'To pay'}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
